import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

// Replace 'CustomTable' with the name of your custom table component
import { Box, IconButton, Link, MenuItem, useTheme } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Stack from '@mui/system/Stack';


import { fDate } from 'src/utils/format-time';
import { KanbanColorMap } from 'src/utils/jobs-kanban';

import { useTranslate } from 'src/locales';
import { Placement } from 'src/services/applications/applications.types';

import _, { get } from 'lodash';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { TenantType } from 'src/types/enums';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  row: Placement;
  onEditPlacement: (placementId: string) => void;
};

// ----------------------------------------------------------------------

const PlacementsTableRow = ({
  row,
  onEditPlacement
}: Props) => {

  const theme = useTheme();

  const { t } = useTranslate();

  const router = useRouter();

  const tenantType = useOrgTenant();

  const popover = usePopover();

  const { enqueueSnackbar } = useSnackbar();

  const viewJob = useCallback(() => {
    if (!row.job) {
      return;
    }

    router.push(paths.dashboard.jobs.detail(row.job.id))
  }, [row.job, router]);

  const viewCompany = useCallback(() => {

    let company = null;
    if (tenantType === TenantType.Recruiter) {
      company = row.employer_company;
    } else if (tenantType === TenantType.Client) {
      company = row.recruiter_company;
    }

    if (company) {
      router.push(paths.dashboard.companies.detail(company.id, tenantType, company.company_type));
    }

  }, [router, tenantType, row]);

  const viewPlacement = useCallback(() => {
    router.push(paths.dashboard.placements.detail(row.id));
  }, [router, row]);

  const getCompanyName = useCallback(() => {
    if (tenantType === TenantType.Recruiter) {
      return row.employer_company_name ?? t('common.unknown');
    } if (tenantType === TenantType.Client) {
      return row.recruiter_company_name ?? t('common.unknown');
    }
    return '';
  }, [row, tenantType, t]);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Link sx={{ color: theme.palette.text.primary, cursor: 'pointer' }} onClick={viewPlacement}>
            <TruncatedTextField
              variant='body2'
              text={`${row.candidate_first_name} ${row.candidate_last_name}`}
              limit={20}
              mode='characters'
              sx={{ fontWeight: 600, lineHeight: 1.2 }}
            />
          </Link>

        </TableCell>

        <TableCell>
          <Link sx={{ color: theme.palette.text.primary, cursor: 'pointer' }} onClick={viewJob}>
            <TruncatedTextField
              variant='body2'
              text={_.capitalize(`${row.job?.title ?? t('common.unknown')}`)}
              limit={30}
              mode='characters'
              sx={{ fontWeight: 600, lineHeight: 1.2 }}
            />
          </Link>
        </TableCell>

        <TableCell>

          <Link sx={{ color: theme.palette.text.primary, cursor: 'pointer' }} onClick={viewCompany}>
            <TruncatedTextField
              variant='body2'
              text={_.capitalize(getCompanyName())}
              limit={30}
              mode='characters'
              sx={{ fontWeight: 600, lineHeight: 1.2 }}
            />
          </Link>

        </TableCell>

        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              width={8}
              height={8}
              borderRadius={4}
              sx={{ backgroundColor: KanbanColorMap[row.application.stage] }}
            />{' '}
            {t(`enums.application_stage.${row.application.stage}`)}
          </Stack>
        </TableCell>

        <TableCell>{fDate(row.placed_at)}</TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>

      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewPlacement();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          {t('common.view')}
        </MenuItem>

        {
          onEditPlacement && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                onEditPlacement(row.id);
              }}
            >
              <Iconify icon="material-symbols:edit" />
              {t('common.edit')}
            </MenuItem>
          )
        }

      </CustomPopover>
    </>
  );
};

export default PlacementsTableRow;
